import { fromBase64Url, randomBytes, toBase64Url, utf8 } from '@realmery/vault';

/**
 * A realm encrypted at rest (docs/REALM-ENCRYPTION.md sections 2 and 3):
 *
 *   "RLMS" | version | header length (u32) | header JSON
 *          | summary iv | summary length (u32) | summary ciphertext
 *          | data iv    | data ciphertext
 *
 * The header (realm uuid and wrapped realm key) is not secret, but it is the additional
 * data of both ciphertexts, with the account's user id, so no part can be swapped
 * between realms or accounts. Everything is AES-256-GCM. The summary is what the realm
 * list shows, readable without decrypting the whole realm.
 */
const MAGIC = utf8('RLMS');
const VERSION = 1;
const IV_BYTES = 12;
const PREFIX_BYTES = MAGIC.length + 1 + 4;
const REALM_KEY_FORMAT = 'realmery-realm-key-1';

export class SealedRealmError extends Error {
  constructor(
    readonly code: 'damaged' | 'wrong_key',
    message: string,
  ) {
    super(message);
    this.name = 'SealedRealmError';
  }
}

export interface SealedHeader {
  /** The realm's storage uuid (db/protocol.ts#realmKey), not the realm's own id. */
  readonly realm: string;
  /** The realm key, wrapped under the account's realms key: base64url of iv | ciphertext. */
  readonly key: string;
}

/** A realm key ready to use: non-extractable, plus its wrapped form for the header. */
export interface RealmKey {
  readonly key: CryptoKey;
  readonly wrapped: string;
}

function keyData(userId: string, realm: string): Uint8Array<ArrayBuffer> {
  return utf8(`${REALM_KEY_FORMAT}\0${userId}\0${realm}`);
}

function importRealmKey(raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** A new random realm key, wrapped under the realms key for this account and realm. */
export async function newRealmKey(realmsKey: CryptoKey, userId: string, realm: string): Promise<RealmKey> {
  const raw = randomBytes(32);
  try {
    return await wrapRealmKey(realmsKey, userId, realm, raw);
  } finally {
    raw.fill(0);
  }
}

/** A realm key from its 32 bytes, wrapped under the realms key for this account and realm (`raw` is left as it was). */
export async function wrapRealmKey(realmsKey: CryptoKey, userId: string, realm: string, raw: Uint8Array<ArrayBuffer>): Promise<RealmKey> {
  if (raw.length !== 32) throw new SealedRealmError('damaged', 'That realm key is damaged.');
  const iv = randomBytes(IV_BYTES);
  const sealed = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: keyData(userId, realm) }, realmsKey, raw));
  return { key: await importRealmKey(raw), wrapped: toBase64Url(concat(iv, sealed)) };
}

// ---- editions (docs/SHARING-PLAN.md section 3.1) ----

/**
 * An edition is sealed like a realm, under its own edition key rather than one wrapped in
 * the header (the header's key is empty: members get the key in a letter), and with the
 * owner and audience where a realm has its account's user id, so an edition can't pass
 * for another owner's or another audience's.
 */
export function editionContext(ownerId: string, audience: string): string {
  return `edition\0${ownerId}\0${audience}`;
}

/** An edition key from its 32 bytes, for sealing or opening an edition. */
export async function editionKey(raw: Uint8Array<ArrayBuffer>): Promise<RealmKey> {
  if (raw.length !== 32) throw new SealedRealmError('wrong_key', 'That edition key is damaged.');
  return { key: await importRealmKey(raw), wrapped: '' };
}

/** The realm key's 32 bytes, unwrapped from the header. The caller zeroes them. */
async function decryptRealmKey(realmsKey: CryptoKey, userId: string, header: SealedHeader): Promise<Uint8Array<ArrayBuffer>> {
  let bytes: Uint8Array<ArrayBuffer>;
  try {
    bytes = fromBase64Url(header.key);
  } catch {
    throw new SealedRealmError('damaged', 'This realm is damaged and cannot be opened.');
  }
  let raw: Uint8Array<ArrayBuffer>;
  try {
    raw = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: bytes.slice(0, IV_BYTES), additionalData: keyData(userId, header.realm) },
        realmsKey,
        bytes.slice(IV_BYTES),
      ),
    );
  } catch {
    throw new SealedRealmError('wrong_key', "This realm was locked by another account's key.");
  }
  return raw;
}

export async function unwrapRealmKey(realmsKey: CryptoKey, userId: string, header: SealedHeader): Promise<RealmKey> {
  const raw = await decryptRealmKey(realmsKey, userId, header);
  try {
    return { key: await importRealmKey(raw), wrapped: header.key };
  } finally {
    raw.fill(0);
  }
}

// ---- co-owners (docs/SHARING-CO-OWNERS-PLAN.md section 3.2) ----

/** The realm key's 32 bytes, for a letter to a co-owner. The caller zeroes them. */
export function rawRealmKey(realmsKey: CryptoKey, userId: string, header: SealedHeader): Promise<Uint8Array<ArrayBuffer>> {
  return decryptRealmKey(realmsKey, userId, header);
}

/**
 * A co-owner's key to the owner's realm: its 32 bytes from their letter, with the
 * header's wrapped key kept as it was, so a version they seal still opens for the owner.
 * They seal with the owner's account id where a realm has its own account's.
 */
export async function coOwnerKey(raw: Uint8Array<ArrayBuffer>, wrapped: string): Promise<RealmKey> {
  if (raw.length !== 32) throw new SealedRealmError('wrong_key', 'That realm key is damaged.');
  return { key: await importRealmKey(raw), wrapped };
}

// ---- edition keys for co-owners (docs/SHARING-CO-OWNERS-PLAN.md section 7) ----

const EDITION_KEY_FORMAT = 'realmery-edition-key-1';

function editionKeyData(ownerId: string, realmId: string, audience: string, keyId: string): Uint8Array<ArrayBuffer> {
  return utf8(`${EDITION_KEY_FORMAT}\0${ownerId}\0${realmId}\0${audience}\0${keyId}`);
}

/**
 * An edition's key sealed under the realm key, so whoever holds the realm key (the
 * owner's devices and co-owners) can make the edition again: the realm key's id, a dot,
 * then base64url of iv | ciphertext. Bound to the owner, realm, audience and realm key.
 */
export async function sealEditionKey(realmKey: Uint8Array<ArrayBuffer>, ownerId: string, realmId: string, audience: string, editionKey: Uint8Array<ArrayBuffer>): Promise<string> {
  if (editionKey.length !== 32) throw new SealedRealmError('damaged', 'That edition key is damaged.');
  const keyId = await keyIdOf(realmKey);
  const iv = randomBytes(IV_BYTES);
  const key = await importRealmKey(realmKey);
  const sealed = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: editionKeyData(ownerId, realmId, audience, keyId) }, key, editionKey));
  return `${keyId}.${toBase64Url(concat(iv, sealed))}`;
}

/** The realm key's id a sealed edition key was sealed under: one the realm no longer has needs sealing again. */
export const sealedUnder = (sealed: string): string => sealed.split('.')[0] ?? '';

/** An edition's key from `sealEditionKey`, or null if it's under another realm key, for another edition, or damaged. */
export async function openEditionKey(realmKey: Uint8Array<ArrayBuffer>, ownerId: string, realmId: string, audience: string, sealed: string): Promise<Uint8Array<ArrayBuffer> | null> {
  const keyId = await keyIdOf(realmKey);
  const [under, body] = sealed.split('.');
  if (under !== keyId || !body) return null;
  try {
    const bytes = fromBase64Url(body);
    const key = await importRealmKey(realmKey);
    const raw = new Uint8Array(
      await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bytes.slice(0, IV_BYTES), additionalData: editionKeyData(ownerId, realmId, audience, keyId) }, key, bytes.slice(IV_BYTES)),
    );
    return raw.length === 32 ? raw : null;
  } catch {
    return null;
  }
}

/** Which key this is, without saying anything of it: so a new one in a letter can be told from the one kept. */
export async function keyIdOf(raw: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', concat(utf8('realmery-key-id-1\0'), raw)));
  return [...digest.slice(0, 8)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function concat(...parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

function u32(n: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, n);
  return out;
}

function partData(prefixAndHeader: Uint8Array, userId: string, part: 'summary' | 'data'): Uint8Array<ArrayBuffer> {
  return concat(prefixAndHeader, utf8(`\0${userId}\0${part}`));
}

/** Seals a realm: its summary (any JSON) and its SQLite file. */
export async function sealRealm(
  userId: string,
  realm: string,
  key: RealmKey,
  summary: unknown,
  data: Uint8Array<ArrayBuffer>,
): Promise<Uint8Array<ArrayBuffer>> {
  const header = utf8(JSON.stringify({ realm, key: key.wrapped } satisfies SealedHeader));
  const head = concat(MAGIC, new Uint8Array([VERSION]), u32(header.length), header);
  const summaryIv = randomBytes(IV_BYTES);
  const sealedSummary = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: summaryIv, additionalData: partData(head, userId, 'summary') },
      key.key,
      utf8(JSON.stringify(summary)),
    ),
  );
  const dataIv = randomBytes(IV_BYTES);
  const sealedData = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: dataIv, additionalData: partData(head, userId, 'data') }, key.key, data),
  );
  return concat(head, summaryIv, u32(sealedSummary.length), sealedSummary, dataIv, sealedData);
}

/** Where each part of a sealed realm is, from its first bytes. */
export interface SealedLayout {
  readonly header: SealedHeader;
  /** Bytes up to and including the header: the additional data's start. */
  readonly headEnd: number;
  /** Where the summary's iv starts, and where the data's iv starts. */
  readonly summaryAt: number;
  readonly dataAt: number;
}

function damaged(): SealedRealmError {
  return new SealedRealmError('damaged', 'This realm is damaged and cannot be opened.');
}

/**
 * Reads the layout from the start of a sealed realm. `prefix` needs to hold the header
 * and the summary; {@link layoutBytesNeeded} says how much that is.
 */
export function readLayout(prefix: Uint8Array): SealedLayout {
  if (prefix.length < PREFIX_BYTES || MAGIC.some((b, i) => prefix[i] !== b)) throw damaged();
  if (prefix[MAGIC.length] !== VERSION) {
    throw new SealedRealmError('damaged', 'This realm was stored by a newer version of Realmery.');
  }
  const view = new DataView(prefix.buffer, prefix.byteOffset, prefix.byteLength);
  const headEnd = PREFIX_BYTES + view.getUint32(MAGIC.length + 1);
  if (prefix.length < headEnd + IV_BYTES + 4) throw damaged();
  let header: SealedHeader;
  try {
    const parsed: unknown = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(prefix.subarray(PREFIX_BYTES, headEnd)));
    if (!parsed || typeof parsed !== 'object') throw damaged();
    const { realm, key } = parsed as Record<string, unknown>;
    if (typeof realm !== 'string' || typeof key !== 'string') throw damaged();
    header = { realm, key };
  } catch {
    throw damaged();
  }
  const summaryAt = headEnd;
  const dataAt = summaryAt + IV_BYTES + 4 + view.getUint32(summaryAt + IV_BYTES);
  return { header, headEnd, summaryAt, dataAt };
}

/**
 * How many leading bytes {@link readLayout} and {@link openSummary} need, given what has
 * been read so far: grows until it stops changing. Lets storage read only the start of
 * a large realm to list it.
 */
export function layoutBytesNeeded(prefix: Uint8Array): number {
  if (prefix.length < PREFIX_BYTES) return PREFIX_BYTES;
  const view = new DataView(prefix.buffer, prefix.byteOffset, prefix.byteLength);
  const headEnd = PREFIX_BYTES + view.getUint32(MAGIC.length + 1);
  if (prefix.length < headEnd + IV_BYTES + 4) return headEnd + IV_BYTES + 4;
  return headEnd + IV_BYTES + 4 + view.getUint32(headEnd + IV_BYTES);
}

async function openPart(
  bytes: Uint8Array,
  layout: SealedLayout,
  userId: string,
  key: RealmKey,
  part: 'summary' | 'data',
): Promise<Uint8Array<ArrayBuffer>> {
  const at = part === 'summary' ? layout.summaryAt : layout.dataAt;
  const start = at + IV_BYTES + (part === 'summary' ? 4 : 0);
  const end = part === 'summary' ? layout.dataAt : bytes.length;
  if (bytes.length < end || end < start) throw damaged();
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: bytes.slice(at, at + IV_BYTES), additionalData: partData(bytes.subarray(0, layout.headEnd), userId, part) },
        key.key,
        bytes.slice(start, end),
      ),
    );
  } catch {
    throw damaged();
  }
}

export async function openSummary(prefix: Uint8Array, layout: SealedLayout, userId: string, key: RealmKey): Promise<unknown> {
  const plain = await openPart(prefix, layout, userId, key, 'summary');
  return JSON.parse(new TextDecoder().decode(plain)) as unknown;
}

export function openData(bytes: Uint8Array, layout: SealedLayout, userId: string, key: RealmKey): Promise<Uint8Array<ArrayBuffer>> {
  return openPart(bytes, layout, userId, key, 'data');
}
